import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAuth } from 'react-oidc-context';
import { useAppEnvironment } from '../../../contexts/app-environment-context';
import type { IUser } from '../../../interfaces/user';
import type { UserRole } from '../../generated/client';
import { getUserDetails } from '../../generated/client';
import { queryKeys } from '../../query-keys';

export function useUserDetails(): UseQueryResult<IUser, AxiosError> {
    const { isAuthenticated } = useAuth();
    const { localStore } = useAppEnvironment();

    const cleanupUserRoles = (roles: UserRole[]): UserRole[] => {
        if (roles.includes('SYSTEM')) {
            const filteredRoles = roles.filter((role) => role !== 'SYSTEM');

            if (!roles.includes('RM') && !roles.includes('EVERYONE')) return [...filteredRoles, 'EVERYONE'];

            return filteredRoles;
        }

        return roles;
    };

    return useQuery({
        queryKey: queryKeys.user(),
        queryFn: ({ signal }) => getUserDetails({ signal }).then((result) => result.data),
        enabled: isAuthenticated,
        select: (user) => {
            const persistedRole: UserRole = localStore.retrieveUserRole();
            const roles: UserRole[] = user?.roles?.length ? user.roles : ['EVERYONE'];
            const userRoles: UserRole[] = cleanupUserRoles(roles);
            const newUser: IUser = {
                userName: user!.userName,
                roles: userRoles,
                currentRole: userRoles.includes(persistedRole) ? persistedRole : userRoles[0],
            };
            localStore.storeUserRole(newUser.currentRole!);

            return newUser;
        },
    });
}
